import { selectItems } from '@/redux/data/dataSlice';
import { ItemType } from '@/redux/data/dataTypes';
import { useAppSelector } from '@/redux/hooks';
import { InboxOutlined } from '@ant-design/icons';
import Fuse from 'fuse.js';
import { find, map, size } from 'lodash';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ItemCard from './ItemCard';
import ItemListItem from './ItemListItem';

interface ItemsListProps {
  layout?: 'list' | 'card' | 'compact-card';
  searchTxt?: string;
  selectedCategories?: string[];
  onItemPress: (item: ItemType) => void;
  className?: string;
  cardSize?: 'small' | 'medium' | 'large';
}

const ItemsList: FC<ItemsListProps> = ({
  layout = 'list',
  searchTxt,
  selectedCategories = [],
  onItemPress,
  className,
  cardSize = 'medium',
}) => {
  const { t } = useTranslation();
  const items = useAppSelector(selectItems);

  const filteredList = useMemo(() => {
    let data = items;

    // Filter by categories if any are selected
    if (size(selectedCategories) > 0) {
      data = data.filter((item) => find(selectedCategories, (s) => s === item.categoryId));
    }

    // Apply search if text is provided
    if (searchTxt && searchTxt.length >= 3) {
      const fuse = new Fuse(data, {
        includeScore: false,
        isCaseSensitive: false,
        threshold: 0.3,
        keys: ['name', 'SKU', 'barcode'],
      });
      return fuse.search(searchTxt).map((d) => d.item);
    }

    return data;
  }, [items, searchTxt, selectedCategories]);

  if (filteredList.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-full p-8 bg-[#f6f8fc] dark:bg-gray-700 rounded-xl">
        <div className="text-center p-8 rounded-xl bg-white dark:bg-gray-600 shadow-sm">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 flex items-center justify-center bg-[#e6f7ff] dark:bg-gray-500 text-frenchBlue dark:text-white rounded-full">
              <InboxOutlined className="text-2xl" />
            </div>
          </div>
          <div className="text-lg font-semibold text-gray-800 dark:text-white mb-1">
            {t('common:no_items_found')}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-300">
            Try adjusting your search or filters
          </div>
        </div>
      </div>
    );
  }

  // Different grid layouts based on layout type
  if (layout === 'card') {
    return (
      <div
        className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2 ${className}`}
      >
        {map(filteredList, (item) => (
          <ItemCard key={item.ID} item={item} onItemPress={onItemPress} size={cardSize} />
        ))}
      </div>
    );
  }

  // Compact card layout with smaller cards and tighter grid
  if (layout === 'compact-card') {
    return (
      <div
        className={`grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 xl:grid-cols-12 gap-1.5 ${className}`}
      >
        {map(filteredList, (item) => (
          <ItemCard
            key={item.ID}
            item={item}
            onItemPress={onItemPress}
            size="small"
            className="w-full"
          />
        ))}
      </div>
    );
  }

  // Default list layout
  return (
    <div className={`bg-[#f6f8fc] dark:bg-gray-700 rounded-xl p-1 ${className} scrollable`}>
      {map(filteredList, (item) => (
        <ItemListItem key={item.ID} item={item} onItemPress={onItemPress} />
      ))}
    </div>
  );
};

export default ItemsList;
